import React, { useCallback, useEffect, useState } from "react";
import DataGrid, { Column, Paging } from "devextreme-react/data-grid";
import { Button } from "devextreme-react/button";
import { deleteAppBroadcast, getAppBroadcasts } from "../../api/apiServiceFetch";
import { audienceLabel, formatCount, sexLabel } from "./broadcastOptions";

/**
 * История рассылок в приложении. Сообщение хранится одной строкой, поэтому
 * отзывается у всех получателей разом — вместе с ним снимаются и неотправленные
 * уведомления о нём.
 */
const AppBroadcastsHistory = ({ reloadToken }) => {
	const [jobs, setJobs] = useState([]);
	const [isLoading, setIsLoading] = useState(false);
	const [deletingId, setDeletingId] = useState(null);
	// Пробы в истории выглядели бы рассылкой на одного человека, поэтому их здесь
	// нет. Показать их можно только нарочно — чтобы найти забытую пробу и убрать
	// её из чужого чата.
	const [includeTests, setIncludeTests] = useState(false);
	const [status, setStatus] = useState(null); // { ok, text }

	const load = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await getAppBroadcasts(100, 0, includeTests);
			setJobs(Array.isArray(data) ? data : []);
		} catch (error) {
			console.error("getAppBroadcasts failed:", error);
			setStatus({
				ok: false,
				text: `Не удалось загрузить историю: ${error?.message || "запрос не удался"}`,
			});
		} finally {
			setIsLoading(false);
		}
	}, [includeTests]);

	useEffect(() => {
		load();
	}, [load, reloadToken]);

	const handleDelete = async (job) => {
		if (!job?.id) return;

		// Проба — та же строка рассылки, но получатель один: спрашивать про
		// «всех получателей» было бы пугалкой на пустом месте.
		const question = job.isTest
			? `Отозвать пробную отправку №${job.id}? Объявление исчезнет из чата того человека, которому её клали.`
			: `Отозвать рассылку №${job.id}? Она исчезнет из чатов у всех получателей (${formatCount(Number(job.totalUsers ?? 0))}).${
					job.imageUrl ? " Картинка будет удалена из хранилища." : ""
				} Уже отправленные уведомления не отзываются.`;

		if (!window.confirm(question)) {
			return;
		}

		setDeletingId(job.id);
		setStatus(null);
		try {
			const result = await deleteAppBroadcast(job.id);
			const cancelled = Number(result?.cancelledPushes ?? 0);
			setStatus({
				ok: true,
				text: cancelled
					? `Рассылка №${job.id} отозвана, снято неотправленных уведомлений: ${formatCount(cancelled)}.`
					: `Рассылка №${job.id} отозвана.`,
			});
			load();
		} catch (error) {
			console.error("deleteAppBroadcast failed:", error);
			setStatus({
				ok: false,
				text: `Не удалось отозвать рассылку №${job.id}: ${error?.message || "запрос не удался"}`,
			});
		} finally {
			setDeletingId(null);
		}
	};

	return (
		<div className="broadcasts-page__section">
			<div className="broadcasts-page__section-head">
				<h2>История: рассылки в приложении</h2>
				<div style={{ display: "flex", alignItems: "center", gap: 16 }}>
					<label
						className="broadcasts-page__hint broadcasts-page__hint--beside"
						style={{ display: "flex", alignItems: "center", gap: 6 }}
					>
						<input
							type="checkbox"
							checked={includeTests}
							disabled={isLoading}
							onChange={(e) => setIncludeTests(e.target.checked)}
						/>
						Показать пробные отправки
					</label>
					<Button text="Обновить" onClick={load} disabled={isLoading} />
				</div>
			</div>

			{status && (
				<p
					className={`broadcasts-page__status ${
						status.ok ? "is-ok" : "is-error"
					}`}
					style={{ marginTop: 0, marginBottom: 12 }}
				>
					{status.text}
				</p>
			)}

			<DataGrid
				dataSource={jobs}
				keyExpr="id"
				showBorders={true}
				rowAlternationEnabled={true}
			>
				<Paging defaultPageSize={10} />
				<Column dataField="id" caption="ID" width={70} />
				{/* Колонка появляется только вместе с пробами: в обычной истории
				    она была бы столбцом из одинаковых прочерков. */}
				{includeTests && (
					<Column
						dataField="isTest"
						caption="Тип"
						width={90}
						calculateCellValue={(d) => (d.isTest ? "проба" : "рассылка")}
					/>
				)}
				<Column
					dataField="audienceType"
					caption="Аудитория"
					width={190}
					calculateCellValue={(d) => audienceLabel(d.audienceType)}
				/>
				<Column
					dataField="sex"
					caption="Пол"
					width={110}
					calculateCellValue={(d) => sexLabel(d.sex)}
				/>
				<Column dataField="totalUsers" caption="Получателей" width={120} />
				<Column
					dataField="readCount"
					caption="Прочитали"
					width={120}
					calculateCellValue={(d) => `${d.readCount ?? 0} из ${d.totalUsers ?? 0}`}
				/>
				{/* Видно, сообщали ли о рассылке пушем: без этого непонятно, почему
				    одну заметили, а другую нет. */}
				<Column
					dataField="pushBroadcastId"
					caption="Уведомление"
					width={130}
					calculateCellValue={(d) =>
						d.pushBroadcastId ? `задание №${d.pushBroadcastId}` : "без пуша"
					}
				/>
				<Column
					dataField="createdAt"
					caption="Создана"
					dataType="datetime"
					format="dd.MM.yyyy HH:mm"
					width={160}
				/>
				{/* Рассылка бывает и из одной картинки: без этой колонки строка
				    выглядела бы пустой, будто отправили ничего. */}
				<Column
					caption="Картинка"
					width={90}
					allowSorting={false}
					cellRender={({ data }) =>
						data.imageUrl ? (
							<a href={data.imageUrl} target="_blank" rel="noreferrer">
								<img
									src={data.imageUrl}
									alt="Картинка рассылки"
									style={{
										width: 56,
										height: 56,
										objectFit: "cover",
										borderRadius: 6,
									}}
								/>
							</a>
						) : (
							""
						)
					}
				/>
				<Column dataField="message" caption="Сообщение" />
				<Column
					caption="Действие"
					width={160}
					cellRender={({ data }) => (
						<Button
							text={deletingId === data.id ? "..." : "Отозвать"}
							disabled={deletingId === data.id}
							onClick={() => handleDelete(data)}
							type="danger"
							stylingMode="outlined"
						/>
					)}
				/>
			</DataGrid>
		</div>
	);
};

export default AppBroadcastsHistory;
